import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';

/**
 * TasParticipant action request builders.
 *
 * Replaces the duplicated custom methods on the near-identical
 * `tio-employee/app/adapters/tas-participant.ts` and
 * `tio-admin/app/adapters/tas-participant.ts`. The two adapter copies reduce
 * to a SINGLE seam:
 *
 * - **demoMode (seam B)** — the employee adapter consulted
 *   `demoMode.demoJsonFor(url, 'GET')` BEFORE the two eligibility GETs because
 *   its raw `this.ajax` bypassed the request-manager demo handler. Routing the
 *   call sites through `store.request(...)` removes that need: the demo seam is
 *   honoured natively (the terminal `Fetch` handler's `window.fetch` is
 *   intercepted by `DemoModeService`, and the eligibility fetch-matchers in
 *   `tio-employee/app/demo/products/tas-fixtures.ts` match by `urlIncludes`
 *   `/dynamic-eligibility` and `/periods-of-eligibility`). A byte-identical URL
 *   therefore preserves demo behaviour with NO explicit guard at the call site.
 *
 * NEITHER adapter method carries a `by:{name,userId,actingWithRole}` actor — the
 * state transitions send ONLY `{ data: { attributes: { _changelog } } }` — so
 * there is NO `tas-actor` seam in this module (unlike `tas-application-actions`).
 *
 * The `programCode` filtering the legacy eligibility methods applied to the
 * RESPONSE (`return programCode ? allEligibilities[programCode] : all`) is NOT a
 * request concern — the GET wire is identical with or without it. That filter
 * stays at each call site (it reads `response.content[programCode]`); these
 * builders emit only the raw GET.
 */

const tasParticipantsUrlOptions: QueryUrlOptions = {
  identifier: { type: 'tas-participants' },
  op: 'query',
  resourcePath: 'tas-participants',
};

function tasParticipantsUrl(path?: string): string {
  const base = buildBaseURL(tasParticipantsUrlOptions);
  return path ? `${base}/${path.replace(/^\/+/, '')}` : base;
}

/** The transient, write-only changelog the TAS API records on a transition. */
export interface TasParticipantChangelog {
  note: string;
}

/**
 * PATCH /tas-participants/:id/transition/state/hold
 * body `{ data: { attributes: { _changelog } } }`
 *
 * Mirrors `TasParticipantAdapter#holdTasParticipant`, which built the URL via
 * `this.buildURL('tas-participants', tasParticipantId) + '/transition/state/hold'`
 * and sent
 * `this.ajax(url, 'PATCH', { data: { data: { attributes: { _changelog: changelog } } } })`
 * (Ember Data's `data` option IS the request body, so the wire body is the
 * inner `{ data: { attributes: { _changelog } } }`).
 */
export function holdTasParticipantRequest(
  id: string,
  changelog?: TasParticipantChangelog
): RequestInfo {
  return {
    url: tasParticipantsUrl(`${id}/transition/state/hold`),
    method: 'PATCH',
    body: JSON.stringify({ data: { attributes: { _changelog: changelog } } }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /tas-participants/:id/transition/state/activate
 * body `{ data: { attributes: { _changelog } } }`
 *
 * Mirrors `TasParticipantAdapter#removeTasParticipantHold`, which built the URL
 * via `this.buildURL('tas-participants', tasParticipantId) + '/transition/state/activate'`
 * and sent
 * `this.ajax(url, 'PATCH', { data: { data: { attributes: { _changelog: changelog } } } })`.
 */
export function activateTasParticipantRequest(
  id: string,
  changelog?: TasParticipantChangelog
): RequestInfo {
  return {
    url: tasParticipantsUrl(`${id}/transition/state/activate`),
    method: 'PATCH',
    body: JSON.stringify({ data: { attributes: { _changelog: changelog } } }),
    cacheOptions: { [SkipCache]: true },
  };
}

export type TasParticipantEligibilityStrategy = 'manual-intervention' | 'eligibility-service';

/**
 * PATCH /tas-participants/:id/transition/eligibility-strategy/:strategy
 * body `{ data: { attributes: { _changelog } } }`
 *
 * Mirrors `TasParticipantAdapter#updateEligibilityStrategy`, which built the URL
 * via `this.buildURL('tas-participants', tasParticipantId) + '/transition/eligibility-strategy/' + eligibilityStrategy`
 * and sent
 * `this.ajax(url, 'PATCH', { data: { data: { attributes: { _changelog: changelog } } } })`.
 */
export function updateTasParticipantEligibilityStrategyRequest(
  id: string,
  strategy: TasParticipantEligibilityStrategy,
  changelog: TasParticipantChangelog
): RequestInfo {
  return {
    url: tasParticipantsUrl(`${id}/transition/eligibility-strategy/${strategy}`),
    method: 'PATCH',
    body: JSON.stringify({ data: { attributes: { _changelog: changelog } } }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * GET /tas-participants/:id/dynamic-eligibility
 *
 * Mirrors `TasParticipantAdapter#dynamicEligibility`, which built the URL via
 * `this.buildURL('tas-participants', tasParticipantId) + '/dynamic-eligibility'`
 * and sent `this.ajax(url, 'GET')` (raw JSON keyed by program code). The
 * optional `programCode` filter the adapter applied to the response stays at the
 * call site — see the module docblock.
 */
export function dynamicEligibilityRequest(id: string): RequestInfo {
  return {
    url: tasParticipantsUrl(`${id}/dynamic-eligibility`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * GET /tas-participants/:id/periods-of-eligibility
 *
 * Mirrors `TasParticipantAdapter#periodOfEligibility`, which built the URL via
 * `this.buildURL('tas-participants', tasParticipantId) + '/periods-of-eligibility'`
 * and sent `this.ajax(url, 'GET')` (raw JSON keyed by program code). The
 * optional `programCode` filter the adapter applied to the response stays at the
 * call site — see the module docblock.
 */
export function periodsOfEligibilityRequest(id: string): RequestInfo {
  return {
    url: tasParticipantsUrl(`${id}/periods-of-eligibility`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}
